import { browserHistory } from '../../history';
import { assert } from './debug';

type UpdateCallback = (url: string) => void;

export interface TestSharedHistoryLocationOptions {
  rootURL?: string;
}

/**
 * Test utility for mocking history location in tests
 * @internal - For testing purposes only
 */
export class TestSharedHistoryLocation {
  updateCallback: UpdateCallback = function () {
    // will be set by the test
  };
  rootURL: string;
  _previousURL?: string;

  private history = browserHistory;

  constructor(options: TestSharedHistoryLocationOptions = {}) {
    this.rootURL = options.rootURL ?? '/';

    assert(
      'rootURL must end with a trailing forward slash e.g. "/app/"',
      this.rootURL.at(this.rootURL.length - 1) === '/'
    );
  }

  fullPath(): string {
    return this.history.location.pathname + this.history.location.search;
  }

  getURL(): string {
    const fullPath = this.fullPath();
    return this._stripRootURL(fullPath);
  }

  setURL(path: string): void {
    const formattedPath = this.formatURL(path);
    const currentPath = this.fullPath();
    if (currentPath !== formattedPath) {
      this.history.push(formattedPath);
    }
  }

  replaceURL(path: string): void {
    const formattedPath = this.formatURL(path);
    const currentPath = this.fullPath();
    if (currentPath !== formattedPath) {
      this.history.replace(formattedPath);
    }
  }

  onUpdateURL(callback: UpdateCallback): void {
    this.updateCallback = callback;
  }

  handleURL(url: string): void {
    this.setURL(url);
    this.updateCallback(url);
  }

  formatURL(url: string): string {
    let { rootURL } = this;
    if (url !== '') {
      rootURL = rootURL.replace(/\/$/, '');
    }
    return `${rootURL}${url}`;
  }

  private _stripRootURL(fullPath: string): string {
    let { rootURL } = this;
    rootURL = rootURL.replace(/\/$/, '');
    return fullPath.replace(new RegExp(`^${rootURL}(?=/|\\?|$)`), '') || '/';
  }

  get path(): string {
    return this.getURL();
  }
}
